import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { FormField, FormInput, FormSelect, FormSectionTitle, DatePicker } from '@practium/ui';
import { RichTextarea } from '../../../../recommendations';
import { FloraUploadZone } from '../../FloraUploadZone/FloraUploadZone';
import type { AnalysisPageMode, SoilAnalysisFormData, SoilAnalysisValueKey } from '../../../types/analyses.types';
import type { OcrSoilSample } from '@practium/features/api';
import {
	BRAZILIAN_STATES,
	SOIL_TEXTURE_OPTIONS,
	SOIL_TEXTURE_MODE_OPTIONS,
	UNIT_OPTIONS_MG_DM3,
	UNIT_OPTIONS_PERCENT,
	UNIT_OPTIONS_PH,
	UNIT_OPTIONS_CA_MG_AL,
	UNIT_OPTIONS_HAL_SB_CTC,
	UNIT_OPTIONS_K_NA,
	UNIT_OPTIONS_CAMG,
	UNIT_OPTIONS_PREM,
} from '../../../constants/analyses.constants';
import { useSoilAnalysisForm } from '../../../hooks/useSoilAnalysisForm';
import { useOcrImport } from '../../../hooks/useOcrImport';
import { useOcrFormSync } from '../../../hooks/useOcrFormSync';
import { useLocationOptions } from '../../../hooks/useLocationOptions';
import { mapOcrSampleToSoilForm } from '../../../utils';
import { resolveLabel, formatIsoDate, formatAnalysisValue } from '../../../utils/analysisFormatters';
import { AnalysisValueInput } from '../AnalysisValueInput';
import { LocationFormSection } from '../LocationFormSection';
import { OcrCounter } from '../OcrCounter';
import styles from '../AnalysisForm.module.css';
import { useOcrQueue } from '../../../contexts/OcrQueueContext';

export interface SoilAnalysisFormRef {
	submit: () => Promise<void>;
	discardImport: () => void;
	isDirty: () => boolean;
}

interface SoilAnalysisFormProps {
	mode: AnalysisPageMode;
	initialData?: SoilAnalysisFormData;
	analysisId?: string;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
	onOcrBusyChange?: (busy: boolean) => void;
	ocrSample?: OcrSoilSample;
	ocrFileName?: string;
	ocrSampleIndex?: number;
	ocrSampleTotal?: number;
}

export const SoilAnalysisForm = forwardRef<SoilAnalysisFormRef, SoilAnalysisFormProps>(
	function SoilAnalysisForm(
		{ mode, initialData, ocrSample, ocrFileName, ocrSampleIndex, ocrSampleTotal, analysisId, onSubmit, onLoadingChange, onOcrBusyChange },
		ref
	) {
		const { addFiles } = useOcrQueue();
		const appliedOcrSampleRef = useRef<OcrSoilSample | undefined>(undefined);
		const { form, errors, isLoading, set, populate, handleSubmit, isDirty, syncBaselineField } =
			useSoilAnalysisForm({
				initialData,
				analysisId,
				onLoadingChange,
			});

		const ocr = useOcrImport<OcrSoilSample>('soil', !analysisId);
		useEffect(() => {
			if (ocrSample && !analysisId && appliedOcrSampleRef.current !== ocrSample) {
				appliedOcrSampleRef.current = ocrSample;
				populate(mapOcrSampleToSoilForm(form, ocrSample));
			}
		}, [analysisId, form, ocrSample, populate]);

		const { carryOverRef, handleOcrFileSelect, ocrLoadingMessage, showOcrCounter, currentOcrSample, totalOcrSamples, ocrProgress } =
			useOcrFormSync({
				ocr,
				form,
				populate,
				mapSampleToForm: mapOcrSampleToSoilForm,
				analysisId,
				isLoading,
				onLoadingChange,
				onOcrBusyChange,
			});

		const { properties, areas, propertyOptions, areaOptions, sectorOptions, selectedSectorNames } =
			useLocationOptions(form);

		const stateAutoFillAppliedRef = useRef(false);
		useEffect(() => {
			if (!form.property) return;
			const property = properties.find((item: any) => item._id === form.property);
			const propertyState = property?.state ?? property?.address?.state ?? '';
			set('state', propertyState);
			if (!stateAutoFillAppliedRef.current && properties.length > 0) {
				stateAutoFillAppliedRef.current = true;
				syncBaselineField('state', propertyState);
			}
		}, [form.property, properties, set, syncBaselineField]);

		useImperativeHandle(
			ref,
			() => ({
				submit: async () => {
					const willAdvance = ocr.currentIndex + 1 < ocr.totalSamples;
					const snapshot = form;
					const saved = await handleSubmit({ showSuccessToast: !willAdvance });
					if (!saved) return;
					if (willAdvance) {
						carryOverRef.current = snapshot;
						ocr.advance();
						return;
					}
					ocr.reset();
					onSubmit();
				},
				discardImport: () => ocr.reset(),
				isDirty,
			}),
			[form, handleSubmit, ocr, onSubmit, carryOverRef, isDirty]
		);

		const setUnit = (key: keyof SoilAnalysisFormData['units'], unit: string) =>
			set('units', { ...form.units, [key]: unit });

		if (mode === 'view') {
			return (
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Identificação e Localização</FormSectionTitle>
						<div className={styles.viewGrid}>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Nome da Amostra</span>
								<span className={styles.viewValue}>{form.name || '—'}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Estado</span>
								<span className={styles.viewValue}>
									{form.state ? resolveLabel(BRAZILIAN_STATES, form.state) : '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Data da Coleta</span>
								<span className={styles.viewValue}>{formatIsoDate(form.sampledAt)}</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Propriedade</span>
								<span className={styles.viewValue}>
									{properties.find((p: any) => p._id === form.property)?.name || '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Área</span>
								<span className={styles.viewValue}>
									{areas.find((a: any) => (a._id ?? a.name) === form.area)?.name || '—'}
								</span>
							</div>
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Setor</span>
								<span className={styles.viewValue}>{selectedSectorNames || '—'}</span>
							</div>
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Nutrientes e Matéria Orgânica</FormSectionTitle>
						<div className={styles.viewGrid3}>
							{(
								[
									['Pm1', 'P Mehlich-1 (Fósforo)'],
									['Pres', 'P Resina (Fósforo)'],
									['K', 'K (Potássio)'],
									['Ca', 'Ca (Cálcio)'],
									['Mg', 'Mg (Magnésio)'],
									['S', 'S (Enxofre)'],
									['Na', 'Na (Sódio)'],
									['B', 'B (Boro)'],
									['Cu', 'Cu (Cobre)'],
									['Fe', 'Fe (Ferro)'],
									['Mn', 'Mn (Manganês)'],
									['Zn', 'Zn (Zinco)'],
									['MO', 'Matéria Orgânica'],
								] as [SoilAnalysisValueKey, string][]
							).map(([key, label]) => (
								<div key={key} className={styles.viewField}>
									<span className={styles.viewLabel}>{label}</span>
									<span className={styles.viewValue}>
										{form[key] ? `${formatAnalysisValue(form[key])} ${form.units[key]}` : '—'}
									</span>
								</div>
							))}
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Acidez e Troca Catiônica</FormSectionTitle>
						<div className={styles.viewGrid3}>
							{(
								[
									['pHagua', 'pH Água'],
									['pHCaCl2', 'pH CaCl2'],
									['Al', 'Al³⁺ (Alumínio)'],
									['HAl', 'H + Al (Acidez Potencial)'],
									['Na', 'Na (Sódio)'],
								] as [SoilAnalysisValueKey, string][]
							).map(([key, label]) => (
								<div key={key} className={styles.viewField}>
									<span className={styles.viewLabel}>{label}</span>
									<span className={styles.viewValue}>
										{form[key] ? `${formatAnalysisValue(form[key])} ${form.units[key]}` : '—'}
									</span>
								</div>
							))}
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Complexo Sortivo e Saturações</FormSectionTitle>
						<div className={styles.viewGrid3}>
							{(
								[
									['SB', 'SB (Soma de Bases)'],
									['ctcE', 'CTC (t)'],
									['ctcT', 'CTC (T)'],
									['CaMg', 'Relação Ca/Mg'],
									['satSB', 'V% (Bases)'],
									['satAl', 'm% (Alumínio)'],
									['satCa', 'Saturação Ca%'],
									['satMg', 'Saturação Mg%'],
									['satK', 'Saturação K%'],
								] as [SoilAnalysisValueKey, string][]
							).map(([key, label]) => (
								<div key={key} className={styles.viewField}>
									<span className={styles.viewLabel}>{label}</span>
									<span className={styles.viewValue}>
										{form[key] ? `${formatAnalysisValue(form[key])} ${form.units[key]}` : '—'}
									</span>
								</div>
							))}
							<div className={styles.viewField}>
								<span className={styles.viewLabel}>Textura</span>
								<span className={styles.viewValue}>
									{form.textureMode === 'Prem'
										? `P-rem: ${formatAnalysisValue(form.Prem) || '—'}`
										: form.textureMode === 'texture'
											? resolveLabel(SOIL_TEXTURE_OPTIONS, form.texture)
											: form.textureMode === 'clay'
												? `${formatAnalysisValue(form.clay) || '—'}% Argila`
												: '—'}
								</span>
							</div>
						</div>
					</div>

					{form.note && (
						<div className={styles.section}>
							<FormSectionTitle>Notas de Observação</FormSectionTitle>
							<span className={styles.viewText} dangerouslySetInnerHTML={{ __html: form.note }} />
						</div>
					)}
				</div>
			);
		}

		return (
			<div className={styles.form}>
				<FloraUploadZone
					label="Clique ou arraste o laudo do laboratório (PDF/XLS)"
					onFileSelect={handleOcrFileSelect}
					onFilesSelect={(files) => addFiles(files, 'soil')}
					disabled={ocr.isBusy}
					loading={ocr.isBusy}
					loadingMessage={ocrLoadingMessage}
					blocked={ocr.hasPendingImport}
				/>

				<div className={styles.section}>
					<FormSectionTitle>Identificação e Localização</FormSectionTitle>
					<div className={styles.fields}>
						<FormField label="Nome da Amostra" required error={errors.name}>
							<FormInput
								placeholder="Insira o nome da amostra"
								value={form.name}
								onChange={(e) => set('name', e.target.value)}
								hasError={!!errors.name}
							/>
						</FormField>
						<div className={styles.row2}>
							<FormField label="Estado" error={errors.state}>
								<FormSelect
									clearable
									value={form.state}
									onChange={(v) => set('state', v)}
									options={BRAZILIAN_STATES}
									placeholder="Preenchido pela propriedade"
									hasError={!!errors.state}
									searchable
									disabled
								/>
							</FormField>
							<FormField label="Data da Coleta" required error={errors.sampledAt}>
								<DatePicker
									value={form.sampledAt}
									onChange={(v) => set('sampledAt', v)}
									hasError={!!errors.sampledAt}
								/>
							</FormField>
						</div>
						<LocationFormSection
							property={form.property}
							area={form.area}
							sector={form.sector}
							errors={errors}
							onPropertyChange={(v) => {
								set('property', v);
								set('area', '');
								set('sector', []);
							}}
							onAreaChange={(v) => {
								set('area', v);
								set('sector', []);
							}}
							onSectorChange={(v) => set('sector', v)}
							propertyOptions={propertyOptions}
							areaOptions={areaOptions}
							sectorOptions={sectorOptions}
						/>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Nutrientes e Matéria Orgânica</FormSectionTitle>
					<div className={styles.fields}>
						<div className={styles.row3}>
							{(['Pm1', 'Pres', 'K'] as const).map((key) => (
								<FormField
									key={key}
									label={key === 'Pm1' ? 'P Mehlich-1 (Fósforo)' : key === 'Pres' ? 'P Resina (Fósforo)' : 'K (Potássio)'}
									error={errors[key]}
								>
									<AnalysisValueInput
										value={form[key]}
										unit={form.units[key]}
										onValueChange={(value) => set(key, value)}
										onUnitChange={(unit) => setUnit(key, unit)}
										hasError={!!errors[key]}
										options={key === 'K' ? UNIT_OPTIONS_K_NA : UNIT_OPTIONS_MG_DM3}
									/>
								</FormField>
							))}
						</div>
						<div className={styles.row3}>
							{(['Ca', 'Mg', 'S'] as const).map((key) => (
								<FormField
									key={key}
									label={key === 'Ca' ? 'Ca (Cálcio)' : key === 'Mg' ? 'Mg (Magnésio)' : 'S (Enxofre)'}
									error={errors[key]}
								>
									<AnalysisValueInput
										value={form[key]}
										unit={form.units[key]}
										onValueChange={(value) => set(key, value)}
										onUnitChange={(unit) => setUnit(key, unit)}
										hasError={!!errors[key]}
										options={key === 'Ca' || key === 'Mg' ? UNIT_OPTIONS_CA_MG_AL : UNIT_OPTIONS_MG_DM3}
									/>
								</FormField>
							))}
						</div>
						<div className={styles.row3}>
							<FormField label="B (Boro)" error={errors.B}>
								<AnalysisValueInput
									step="0.01"
									value={form.B}
									unit={form.units.B}
									onValueChange={(value) => set('B', value)}
									onUnitChange={(unit) => setUnit('B', unit)}
									hasError={!!errors.B}
									options={UNIT_OPTIONS_MG_DM3}
								/>
							</FormField>
							<FormField label="Cu (Cobre)" error={errors.Cu}>
								<AnalysisValueInput
									step="0.01"
									value={form.Cu}
									unit={form.units.Cu}
									onValueChange={(value) => set('Cu', value)}
									onUnitChange={(unit) => setUnit('Cu', unit)}
									hasError={!!errors.Cu}
									options={UNIT_OPTIONS_MG_DM3}
								/>
							</FormField>
							<FormField label="Fe (Ferro)" error={errors.Fe}>
								<AnalysisValueInput
									value={form.Fe}
									unit={form.units.Fe}
									onValueChange={(value) => set('Fe', value)}
									onUnitChange={(unit) => setUnit('Fe', unit)}
									hasError={!!errors.Fe}
									options={UNIT_OPTIONS_MG_DM3}
								/>
							</FormField>
						</div>
						<div className={styles.row3}>
							<FormField label="Mn (Manganês)" error={errors.Mn}>
								<AnalysisValueInput
									value={form.Mn}
									unit={form.units.Mn}
									onValueChange={(value) => set('Mn', value)}
									onUnitChange={(unit) => setUnit('Mn', unit)}
									hasError={!!errors.Mn}
									options={UNIT_OPTIONS_MG_DM3}
								/>
							</FormField>
							<FormField label="Zn (Zinco)" error={errors.Zn}>
								<AnalysisValueInput
									value={form.Zn}
									unit={form.units.Zn}
									onValueChange={(value) => set('Zn', value)}
									onUnitChange={(unit) => setUnit('Zn', unit)}
									hasError={!!errors.Zn}
									options={UNIT_OPTIONS_MG_DM3}
								/>
							</FormField>
							<FormField label="Matéria Orgânica" error={errors.MO}>
								<AnalysisValueInput
									value={form.MO}
									unit={form.units.MO}
									onValueChange={(value) => set('MO', value)}
									onUnitChange={(unit) => setUnit('MO', unit)}
									hasError={!!errors.MO}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Acidez e Troca Catiônica</FormSectionTitle>
					<div className={styles.fields}>
						<div className={styles.row3}>
							<FormField label="pH Água" required error={errors.pHagua}>
								<AnalysisValueInput
									min="3.1"
									max="9.9"
									value={form.pHagua}
									unit={form.units.pHagua}
									onValueChange={(value) => set('pHagua', value)}
									onUnitChange={(unit) => setUnit('pHagua', unit)}
									hasError={!!errors.pHagua}
									options={UNIT_OPTIONS_PH}
								/>
							</FormField>
							<FormField label="pH CaCl2" error={errors.pHCaCl2}>
								<AnalysisValueInput
									min="3.1"
									max="9.9"
									value={form.pHCaCl2}
									unit={form.units.pHCaCl2}
									onValueChange={(value) => set('pHCaCl2', value)}
									onUnitChange={(unit) => setUnit('pHCaCl2', unit)}
									hasError={!!errors.pHCaCl2}
									options={UNIT_OPTIONS_PH}
								/>
							</FormField>
							<FormField label="Al³⁺ (Alumínio)" error={errors.Al}>
								<AnalysisValueInput
									value={form.Al}
									unit={form.units.Al}
									onValueChange={(value) => set('Al', value)}
									onUnitChange={(unit) => setUnit('Al', unit)}
									hasError={!!errors.Al}
									options={UNIT_OPTIONS_CA_MG_AL}
								/>
							</FormField>
						</div>
						<div className={styles.row2}>
							<FormField label="H + Al (Acidez Potencial)" error={errors.HAl}>
								<AnalysisValueInput
									value={form.HAl}
									unit={form.units.HAl}
									onValueChange={(value) => set('HAl', value)}
									onUnitChange={(unit) => setUnit('HAl', unit)}
									hasError={!!errors.HAl}
									options={UNIT_OPTIONS_HAL_SB_CTC}
								/>
							</FormField>
							<FormField label="Na (Sódio)" error={errors.Na}>
								<AnalysisValueInput
									value={form.Na}
									unit={form.units.Na}
									onValueChange={(value) => set('Na', value)}
									onUnitChange={(unit) => setUnit('Na', unit)}
									hasError={!!errors.Na}
									options={UNIT_OPTIONS_K_NA}
								/>
							</FormField>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Complexo Sortivo e Saturações</FormSectionTitle>
					<div className={styles.fields}>
						<div className={styles.row3}>
							<FormField label="SB (Soma de Bases)" error={errors.SB}>
								<AnalysisValueInput
									value={form.SB}
									unit={form.units.SB}
									onValueChange={(value) => set('SB', value)}
									onUnitChange={(unit) => setUnit('SB', unit)}
									hasError={!!errors.SB}
									options={UNIT_OPTIONS_HAL_SB_CTC}
								/>
							</FormField>
							<FormField label="CTC (t)" error={errors.ctcE}>
								<AnalysisValueInput
									value={form.ctcE}
									unit={form.units.ctcE}
									onValueChange={(value) => set('ctcE', value)}
									onUnitChange={(unit) => setUnit('ctcE', unit)}
									hasError={!!errors.ctcE}
									options={UNIT_OPTIONS_HAL_SB_CTC}
								/>
							</FormField>
							<FormField label="CTC (T)" error={errors.ctcT}>
								<AnalysisValueInput
									value={form.ctcT}
									unit={form.units.ctcT}
									onValueChange={(value) => set('ctcT', value)}
									onUnitChange={(unit) => setUnit('ctcT', unit)}
									hasError={!!errors.ctcT}
									options={UNIT_OPTIONS_HAL_SB_CTC}
								/>
							</FormField>
						</div>
						<div className={styles.row3}>
							<FormField label="Relação Ca/Mg" error={errors.CaMg}>
								<AnalysisValueInput
									value={form.CaMg}
									unit={form.units.CaMg}
									onValueChange={(value) => set('CaMg', value)}
									onUnitChange={(unit) => setUnit('CaMg', unit)}
									hasError={!!errors.CaMg}
									options={UNIT_OPTIONS_CAMG}
								/>
							</FormField>
							<FormField label="V% (Bases)" error={errors.satSB}>
								<AnalysisValueInput
									value={form.satSB}
									unit={form.units.satSB}
									onValueChange={(value) => set('satSB', value)}
									onUnitChange={(unit) => setUnit('satSB', unit)}
									hasError={!!errors.satSB}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
							<FormField label="m% (Alumínio)" error={errors.satAl}>
								<AnalysisValueInput
									value={form.satAl}
									unit={form.units.satAl}
									onValueChange={(value) => set('satAl', value)}
									onUnitChange={(unit) => setUnit('satAl', unit)}
									hasError={!!errors.satAl}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
						</div>
						<div className={styles.row3}>
							<FormField label="Saturação Ca%" error={errors.satCa}>
								<AnalysisValueInput
									value={form.satCa}
									unit={form.units.satCa}
									onValueChange={(value) => set('satCa', value)}
									onUnitChange={(unit) => setUnit('satCa', unit)}
									hasError={!!errors.satCa}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
							<FormField label="Saturação Mg%" error={errors.satMg}>
								<AnalysisValueInput
									value={form.satMg}
									unit={form.units.satMg}
									onValueChange={(value) => set('satMg', value)}
									onUnitChange={(unit) => setUnit('satMg', unit)}
									hasError={!!errors.satMg}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
							<FormField label="Saturação K%" error={errors.satK}>
								<AnalysisValueInput
									value={form.satK}
									unit={form.units.satK}
									onValueChange={(value) => set('satK', value)}
									onUnitChange={(unit) => setUnit('satK', unit)}
									hasError={!!errors.satK}
									options={UNIT_OPTIONS_PERCENT}
								/>
							</FormField>
						</div>
						<div className={styles.row2}>
							<FormField label="Modo de Textura" required error={errors.textureMode}>
								<FormSelect
									value={form.textureMode}
									onChange={(v) => set('textureMode', v as SoilAnalysisFormData['textureMode'])}
									options={SOIL_TEXTURE_MODE_OPTIONS}
									placeholder="Selecione"
									hasError={!!errors.textureMode}
								/>
							</FormField>
							{form.textureMode === 'Prem' && (
								<FormField label="P-rem" required error={errors.Prem}>
									<AnalysisValueInput
										max="60"
										value={form.Prem}
										unit={form.units.Prem}
										onValueChange={(value) => set('Prem', value)}
										onUnitChange={(unit) => setUnit('Prem', unit)}
										hasError={!!errors.Prem}
										options={UNIT_OPTIONS_PREM}
									/>
								</FormField>
							)}
							{form.textureMode === 'texture' && (
								<FormField label="Classe Textural" required error={errors.texture}>
									<FormSelect
										value={form.texture}
										onChange={(v) => set('texture', v)}
										options={SOIL_TEXTURE_OPTIONS}
										placeholder="Selecione"
										hasError={!!errors.texture}
									/>
								</FormField>
							)}
							{form.textureMode === 'clay' && (
								<FormField label="% Argila" required error={errors.clay}>
									<AnalysisValueInput
										value={form.clay}
										unit={form.units.clay}
										onValueChange={(value) => set('clay', value)}
										onUnitChange={(unit) => setUnit('clay', unit)}
										hasError={!!errors.clay}
										options={UNIT_OPTIONS_PERCENT}
									/>
								</FormField>
							)}
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormField label="Notas de Observação" error={errors.note}>
						<div className={styles.noteWrapper}>
							<RichTextarea
								value={form.note}
								onChange={(v) => set('note', v)}
								placeholder="Insira o conteúdo da nota"
								hasError={!!errors.note}
							/>
						</div>
					</FormField>
				</div>

				{ocrSample && ocrSampleTotal ? (
					<OcrCounter
						current={(ocrSampleIndex ?? 0) + 1}
						total={ocrSampleTotal}
						progress={((ocrSampleIndex ?? 0) + 1) / ocrSampleTotal * 100}
						label={ocrFileName ? `Análises deste ${ocrFileName}` : 'Análises deste PDF'}
					/>
				) : showOcrCounter ? (
					<OcrCounter current={currentOcrSample} total={totalOcrSamples} progress={ocrProgress} />
				) : null}
			</div>
		);
	}
);
